'use client'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

import { Breadcrumbs } from '@/components/breadcrumbs'
import { EASE, HAIRLINE, restoreDashes, useReveal } from '@/components/page-kit'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { PAYROLL } from './data'

/* Payroll's register is the statement: aligned figures, tabular numerals,
   hairline rules. So the page's device is ledger grammar rather than a diagram
   — the deliberate contrast with UDC (a wired bus of parallel modules) and
   HRMS (a directional lifecycle track).

   The hero closes on the four authored figures rather than a module index,
   because for this product the numbers are the claim.

   The h1 appends the audience to the authored title: "Payroll Management
   System" alone says nothing about who it is for, and every term this page
   competes for carries a school or college in it. */

export function PayrollHero() {
  const band = useReveal<HTMLDivElement>(0.25)

  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div className="flex min-h-[80svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+4rem)] sm:pb-20">
          <Breadcrumbs
            items={[
              { name: 'Home', href: '/' },
              { name: 'Products', href: '/products' },
              { name: PAYROLL.title, href: `/products/${PAYROLL.slug}` },
            ]}
          />

          <h1
            className={cn(
              'font-heading mt-6 max-w-4xl font-normal tracking-tight text-foreground',
              'text-[32px]/[1.14] sm:text-[44px]/[1.14] md:text-5xl/[1.12] lg:text-[60px]/[1.1]'
            )}
          >
            <ChromaText>{`${PAYROLL.title} for schools and colleges`}</ChromaText>
          </h1>

          <p className="mt-5 max-w-2xl text-lg font-medium leading-snug text-emerald-600 sm:text-xl md:text-2xl dark:text-primary-active">
            {PAYROLL.tagline}
          </p>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#2B3A46] sm:text-lg dark:text-[#DEE9EE]/90">
            {restoreDashes(PAYROLL.description)}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="#included">See what's included</a>
            </Button>
          </div>
        </div>
      </div>

      {/* ── The figures ────────────────────────────────────────────────────
          A totals row: the rule runs full-bleed, the cells sit in the
          container so the first figure lines up with the headline above. */}
      <div ref={band.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="container mx-auto">
          <dl className="grid grid-cols-2 lg:grid-cols-4">
            {PAYROLL.stats.map((s, i) => (
              <div
                key={s.label}
                className={cn(
                  'relative py-5 sm:py-6',
                  // Rules between cells only, so the band never shows a stray
                  // edge against the container padding.
                  i % 2 === 1 && 'pl-5 sm:pl-6',
                  i > 0 && 'lg:pl-6'
                )}
                style={{
                  opacity: band.shown ? 1 : 0,
                  transform: band.shown ? 'none' : 'translateY(8px)',
                  transition: `opacity 460ms ${EASE} ${i * 80}ms, transform 460ms ${EASE} ${i * 80}ms`,
                }}
              >
                {i > 0 && (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-y-3 left-0 w-px bg-border/70',
                      i % 2 === 0 && 'hidden lg:block'
                    )}
                  />
                )}
                <dd className="font-heading text-2xl font-normal tabular-nums leading-none text-foreground sm:text-3xl">
                  <AnimatedNumber value={s.value} delay={i * 70} />
                </dd>
                <dt className="mt-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  {s.label}
                </dt>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  )
}
